import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from './api';

export function useUserQuery(refetchInterval?: number) {
  return useQuery(
    'user',
    async () => {
      const { data } = await api.get('/api/user');
      return data;
    },
    refetchInterval !== undefined ? { refetchInterval } : undefined
  );
}

export function useStats() {
  return useQuery('stats', async () => {
    const { data } = await api.get('/api/user/stats');
    return data;
  });
}

export function useMessages(limit: number, refetchInterval?: number) {
  return useQuery(
    ['messages', limit],
    async () => {
      const { data } = await api.get(`/api/messages?limit=${limit}`);
      return data;
    },
    refetchInterval !== undefined ? { refetchInterval } : undefined
  );
}

export function useApps() {
  return useQuery('apps', async () => {
    const { data } = await api.get('/api/apps');
    return data;
  });
}

export function useGmailStatus() {
  return useQuery('gmailStatus', async () => {
    const { data } = await api.get('/gmail/status');
    return data;
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation(
    async (settings: Record<string, unknown>) => {
      const { data } = await api.put('/api/user/settings', settings);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('user');
      },
    }
  );
}

export function useAdminUsers(status: string) {
  return useQuery(['adminUsers', status], async () => {
    const { data } = await api.get('/admin/users', { params: { status } });
    return data;
  });
}

export function useApproveUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ userId, planId }: { userId: string; planId?: string }) => {
      const { data } = await api.post(`/admin/users/${userId}/approve`, { planId });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        queryClient.invalidateQueries('adminStats');
      },
    }
  );
}

export function useDenyUser() {
  const queryClient = useQueryClient();
  return useMutation(
    async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/deny`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        queryClient.invalidateQueries('adminStats');
      },
    }
  );
}

export function useChangeUserPlan() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ userId, planId }: { userId: string; planId: string }) => {
      const { data } = await api.patch(`/admin/users/${userId}/plan`, { planId });
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        queryClient.invalidateQueries('adminStats');
      },
    }
  );
}

export function useAdminStats() {
  return useQuery('adminStats', async () => {
    const { data } = await api.get('/admin/stats');
    return data;
  });
}

export function usePlans() {
  return useQuery('plans', async () => {
    const { data } = await api.get('/admin/plans');
    return data;
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();
  return useMutation(
    async (plan: {
      name: string;
      description?: string;
      tokens: number;
      price_monthly?: number;
      features?: string[];
      sort_order?: number;
    }) => {
      const { data } = await api.post('/admin/plans', plan);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('plans');
      },
    }
  );
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ planId, updates }: { planId: string; updates: Record<string, unknown> }) => {
      const { data } = await api.put(`/admin/plans/${planId}`, updates);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('plans');
      },
    }
  );
}

export function useDeletePlan() {
  const queryClient = useQueryClient();
  return useMutation(
    async (planId: string) => {
      const { data } = await api.delete(`/admin/plans/${planId}`);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('plans');
      },
    }
  );
}
